import React, { useCallback, useMemo } from 'react';
import {
  Colors,
  Flex,
  IconChevronRight,
  RemoteImage,
  Tag,
  Typography,
} from '@banhala/ui-kit';
import { useGoodsBasic } from '../../queries/useGoodsBasic';
import { usePreRenderParams } from '../../hooks/usePreRenderParams';
import { useGoodsDetail } from '../../queries/useGoodsDetail';
import {
  bridge,
  getWebBaseURL,
  isNil,
  isNotNil,
  useNavigator,
} from 'shared/src/utils';
import { MarketLikeView } from './MarketLikeView';
import { BeautyBadge } from './components/BeautyBadge';
import { useLogClickMarket } from './hooks/useLogClickMarket';
import { useLogClickBrandHome } from './hooks/useLogClickBrandHome';
import { useLogDisplayBrandHome } from './hooks/useLogDisplayBrandHome';

export const MarketInfoBanner: React.FC = () => {
  const { data: goodsBasic } = useGoodsBasic();
  const { data: goodsDetail } = useGoodsDetail();
  const { data: goodsBasicSkeleton } = usePreRenderParams();

  const logClickMarket = useLogClickMarket();
  const logClickBrandHome = useLogClickBrandHome();
  useLogDisplayBrandHome();

  const marketInfo = useMemo<{
    sno?: number;
    favoriteCount?: number;
    name: string;
    isBrand?: boolean;
  }>(() => {
    if (isNotNil(goodsBasic) && isNotNil(goodsDetail)) {
      const isBrand = goodsBasic.brand !== null;
      return {
        isBrand,
        name:
          goodsBasic.brand && isBrand
            ? goodsBasic.brand.name
            : goodsBasic.market.name,
        sno: goodsBasic.market.sno,
        favoriteCount: goodsDetail.goods.market.score.favorites_count,
      };
    }
    return {
      name: goodsBasicSkeleton?.market.name ?? '',
    };
  }, [goodsBasic, goodsBasicSkeleton?.market, goodsDetail]);

  const { navigator } = useNavigator();

  const onPressMarketName = useCallback(() => {
    if (marketInfo.isBrand) {
      //brand home으로 이동.
      logClickBrandHome();
      bridge.openDeeplink(goodsBasic!.brand!.deeplink, {
        webCallback: async () => {
          const baseURL = await getWebBaseURL();
          location.href = `${baseURL}${goodsBasic?.brand?.deeplink.replace('ably://', '')}`;
        },
      });
      return;
    }

    logClickMarket();

    // bridge.logAnalyticsEvent({name:''})

    if (isNotNil(marketInfo.sno)) {
      navigator.moveToMarketHome({ sno: marketInfo.sno });
    }
  }, [
    marketInfo.isBrand,
    marketInfo.sno,
    logClickMarket,
    logClickBrandHome,
    goodsBasic,
    navigator,
  ]);

  const storeCategoryIcon = goodsDetail?.store_category_icon;
  const showStoreCategoryIcon = isNotNil(storeCategoryIcon);
  const showBeautyIcon =
    !showStoreCategoryIcon &&
    (goodsDetail?.component_visibility.beauty_component?.market ?? false);

  const onPressStoreCategoryIcon = useCallback(() => {
    if (isNil(storeCategoryIcon)) return;

    bridge.logAnalyticsEvent({
      name: 'CLICK_BRAND_DEPARTMENT',
      analytics: storeCategoryIcon.logging?.analytics ?? {},
    });

    bridge.openDeeplink(storeCategoryIcon.deeplink);
  }, [storeCategoryIcon]);

  const onPressBeautyBadge = useCallback(() => {
    bridge.logAnalyticsEvent({
      name: 'CLICK_BEAUTY_DEPARTMENT',
      analytics: {
        TYPE: 'SIMPLE',
      },
    });

    navigator.moveToScreens('BEAUTY_DEPARTMENT');
  }, [navigator]);

  if (showStoreCategoryIcon) {
    return (
      <Flex.Row
        alignItems={'center'}
        justifyContent={'space-between'}
        padding={{ vertical: 12, horizontal: 16 }}
      >
        <Flex.Row alignItems={'center'} gap={2}>
          <Flex onPress={onPressStoreCategoryIcon}>
            <RemoteImage
              uri={storeCategoryIcon.image_url}
              width={storeCategoryIcon.image_width ?? undefined}
              height={storeCategoryIcon.image_height ?? undefined}
            />
          </Flex>
          <IconChevronRight fill={Colors.content_tertiary} size={12} />
          <Flex.Row alignItems={'center'} onPress={onPressMarketName} gap={2}>
            <Typography type={'body2'} color={'content_primary'}>
              {marketInfo.name}
            </Typography>
          </Flex.Row>
        </Flex.Row>
        <MarketLikeView />
      </Flex.Row>
    );
  }

  if (showBeautyIcon) {
    return (
      <Flex.Row
        alignItems={'center'}
        justifyContent={'space-between'}
        padding={{ vertical: 12, horizontal: 16 }}
      >
        <Flex.Row alignItems={'center'} gap={2}>
          <Flex onPress={onPressBeautyBadge}>
            <BeautyBadge />
          </Flex>

          <IconChevronRight fill={Colors.content_tertiary} size={12} />
          <Flex.Row alignItems={'center'} onPress={onPressMarketName} gap={2}>
            <Typography type={'body2'} color={'content_primary'}>
              {marketInfo.name}
            </Typography>
          </Flex.Row>
        </Flex.Row>
        <MarketLikeView />
      </Flex.Row>
    );
  }

  return (
    <Flex.Row
      alignItems={'center'}
      justifyContent={'space-between'}
      padding={{ vertical: 12, horizontal: 16 }}
    >
      <Flex.Row
        testID={'마켓홈이동'}
        alignItems={'center'}
        gap={2}
        onPress={onPressMarketName}
      >
        {marketInfo.isBrand && (
          <Tag color="blue" size="xsmall" type="outline">
            브랜드
          </Tag>
        )}
        <Typography type={'body2'} color={'content_secondary'}>
          {marketInfo.name}
        </Typography>
        <IconChevronRight fill={Colors.content_tertiary} size={16} />
      </Flex.Row>

      {marketInfo.isBrand ? <></> : <MarketLikeView />}
    </Flex.Row>
  );
};
